import React from "react";

import BootstrapNav from "react-bootstrap-v1.6/Nav";
import BootstrapNavItem from "react-bootstrap-v1.6/NavItem";
import BootstrapNavLink from "react-bootstrap-v1.6/NavLink";
import BootstrapTabContainer from "react-bootstrap-v1.6/TabContainer";
import BootstrapTabContent from "react-bootstrap-v1.6/TabContent";
import BootstrapTabPane from "react-bootstrap-v1.6/TabPane";
import BootstrapTab from "react-bootstrap-v1.6/Tab";
import BootstrapTabs from "react-bootstrap-v1.6/Tabs";

import withCustomStyles from "@stadium/ui/src/hoc/custom-styles";

import {
  TabbedViewProps,
  TabNavContainerProps,
  TabNavItemProps,
  TabContentProps,
  TabProps,
  TabNavLinkProps,
  TabContainerProps,
} from "@stadium/ui/types/tabs";

export const TabsContainer = (props: TabContainerProps) => <BootstrapTabContainer {...props} />;

export const TabNavsContainer = ({ styles, ...props }: TabNavContainerProps) => (
  <BootstrapNav {...BootstrapNav.defaultProps} bsPrefix={styles.navContainer} {...props} />
);

export const TabNavItem = ({ styles, ...props }: TabNavItemProps) => (
  <BootstrapNavItem {...BootstrapNavItem.defaultProps} bsPrefix={styles.navItem} {...props} />
);

export const TabContent = ({ styles, ...props }: TabContentProps) => (
  <BootstrapTabContent
    {...BootstrapTabContent.defaultProps}
    bsPrefix={styles.tabContent}
    {...props}
  />
);

export const Tab = ({ styles, ...props }: TabProps) => (
  <BootstrapTabPane {...BootstrapTabPane.defaultProps} bsPrefix={styles.tabPane} {...props} />
);

export const TabNavLink = ({ styles, ...props }: TabNavLinkProps) => (
  <BootstrapNavLink {...BootstrapNavLink.defaultProps} bsPrefix={styles.tabNavLink} {...props} />
);

export { BootstrapTab, BootstrapTabs };

const TabbedView = ({
  renderNavItem: propsRenderNavItems,
  renderTabContent: propsRenderTabContent,
  renderNavContainer: propsRenderNavContainer,
  renderTabsContainer: propsRenderTabsContainer,
  renderTab: propsRenderTab,
  tabs,
  navContainerClassName,
  styles,
  tabContainerProps,
  navContainerProps,
  navItemProps,
  navLinkProps,
  tabContentProps,
  tabProps,
  activeKey,
  defaultActiveKey,
  onSelect,
}: TabbedViewProps) => {
  const renderNavContainer =
    propsRenderNavContainer ||
    ((props) => <TabNavsContainer className={navContainerClassName} styles={styles} {...props} />);
  const renderTabContent =
    propsRenderTabContent || ((props) => <TabContent styles={styles} {...props} />);
  const renderNavItem =
    propsRenderNavItems ||
    ((navItemData) => {
      const { id, label } = navItemData;
      return (
        <TabNavItem key={id} styles={styles} {...navItemProps?.(navItemData)}>
          <TabNavLink
            eventKey={id}
            styles={styles}
            onSelect={onSelect}
            {...navLinkProps?.(navItemData)}
          >
            {label}
          </TabNavLink>
        </TabNavItem>
      );
    });
  const renderTabsContainer = propsRenderTabsContainer || ((props) => <TabsContainer {...props} />);
  const renderTab =
    propsRenderTab ||
    ((tabContentData) => {
      const { id, content } = tabContentData;
      return (
        <Tab
          transition={false}
          eventKey={id}
          key={id}
          styles={styles}
          {...tabProps?.(tabContentData)}
        >
          {content}
        </Tab>
      );
    });

  const tabNavigation = renderNavContainer({
    children: tabs.map((tabData) => renderNavItem(tabData)),
    ...navContainerProps,
  });

  const tabContent = renderTabContent({
    children: tabs.map((tabData) => renderTab(tabData)),
    ...tabContentProps,
  });

  return renderTabsContainer({
    children: (
      <>
        {tabNavigation}
        {tabContent}
      </>
    ),
    activeKey,
    defaultActiveKey,
    ...tabContainerProps,
  });
};

export default withCustomStyles(TabbedView, {
  getStyles({ styleType }) {
    switch (styleType) {
      case "full-page-tab":
        return { styleImport: () => import("./full-page-tab.module.scss") };

      default:
        return { styleImport: () => import("./empty.module.scss") };
    }
  },
});
